/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useEffect } from "react";

export function useValidateReservationCustomers({
  customerData,
  bookerData,
  guestCustomers,
  guestChildren,
  requiredCustomerFieldsReservation,
  settings,
  setCustomerFormData,
  setShowNewCustomerForm,
  setEditingAccompanyingIndex,
  setEditingChildIndex,
  setCustomerData,
  setBookerData,
  setGuestCustomers,
  setGuestChildren,
  pendingMainGuestId
}) {

    useEffect(() => {
        const validateCustomer = async (customer, role, index = null) => {
          if (!customer || !customer.id || !requiredCustomerFieldsReservation[role]) return;
      
          const requiredFields = requiredCustomerFieldsReservation[role];
          const missing = requiredFields.filter(
            (f) => !customer[f] || (typeof customer[f] === "string" && customer[f].trim() === "")
          );
      
          if (missing.length > 0) {
            console.warn(`❌ ${role} ID ${customer.id} tiene campos faltantes:`, missing);
      
            if (role === "booker") {
              const birthDate = customer.birth_date ? new Date(customer.birth_date) : null;
              const minAgeBooker = settings.min_age_booker;
    
              if (birthDate && minAgeBooker) {
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                  age--;
                }
    
                if (age < minAgeBooker) {
                  alert(`❌ The booker must be at least ${minAgeBooker} years old. Current age: ${age}`);
                  return;
                }
              }
    
              setCustomerFormData(customer);
              setShowNewCustomerForm("booker");
              return;
            }
    
      
            if (role === "main_guest") {
              setCustomerFormData(customer);
              setShowNewCustomerForm(true); // main_guest usa el formulario genérico
              return;
            }
      
            if (role === "accompanying") {
              setCustomerFormData(customer);
              setEditingAccompanyingIndex(index);
              setShowNewCustomerForm(`accompanying-${index}`);
              return;
            }
      
            if (role === "child") {
              setCustomerFormData(customer);
              setEditingChildIndex(index);
              setShowNewCustomerForm(`child-${index}`);
              return;
            }
          }
          if (role === "main_guest" && customer.id === pendingMainGuestId.current) {
            pendingMainGuestId.current = null;
          }
          
        };
      
        // MAIN GUEST
        if (customerData?.__manual_insert) {
          validateCustomer(customerData, "main_guest");
          const clean = { ...customerData };
          delete clean.__manual_insert;
          setCustomerData(clean);
        }
      
        // BOOKER
        if (bookerData?.__manual_insert) {
          validateCustomer(bookerData, "booker");
          const clean = { ...bookerData };
          delete clean.__manual_insert;
          setBookerData(clean);
        }
      
        // ACCOMPANYING
        guestCustomers.forEach((g, i) => {
          if (g?.__manual_insert) {
            validateCustomer(g, "accompanying", i);
            const updated = [...guestCustomers];
            delete updated[i].__manual_insert;
            setGuestCustomers(updated);
          }
        });
      
        // CHILD
        guestChildren.forEach((c, i) => {
          if (c?.__manual_insert) {
            validateCustomer(c, "child", i);
            const updated = [...guestChildren];
            delete updated[i].__manual_insert;
            setGuestChildren(updated);
          }
        });
      }, [customerData, bookerData, guestCustomers, guestChildren, requiredCustomerFieldsReservation]);
      
    }